// LineupContext.js
import React, { useState, createContext } from "react";
import { useDispatch, useSelector } from "react-redux";
// import { useNavigate } from "react-router-dom";
import { useToast } from "../hooks/useToast";
import {
  setLineup,
  setLotn,
  setProjectedTotal,
  setAlternateLotn,
  setAlternateSelections,
  resetLineup,
  resetAlternateSelections,
  resetLotn,
  resetAlternateLotn,
  resetProjectedTotal,
} from "../features/lineup/lineupReducer";
import {
  calculateScoreForSlot,
  calculateScores,
} from "../utils/events/FighterScores";
import { useSubmitLineup } from "../hooks/lineup/useSubmitLineup";
import { useFetchUserEventLineup } from "../hooks/lineup/useFetchUserEventLineup";
import { useEventDetails } from "../hooks/events/useEventDetails";

export const LineupContext = createContext();

export const LineupProvider = ({ children }) => {
  const dispatch = useDispatch();
  // const navigate = useNavigate();
  const toast = useToast();

  const [showSubmissionModal, setShowSubmissionModal] = useState(false);
  const userId = useSelector((state) => state.user.userId);
  const leagueId = useSelector((state) => state.league.leagueId);
  const eventId = useSelector((state) => state.event.eventId);
  const eventName = useSelector((state) => state.event.eventName);
  const lineup = useSelector((state) => state.lineup.lineup);
  const lotn = useSelector((state) => state.lineup.lotn);
  const alternateLOTN = useSelector((state) => state.lineup.alternateLOTN);
  const alternateSelections = useSelector(
    (state) => state.lineup.alternateSelections
  );
  const projectedTotal = useSelector((state) => state.lineup.projectedTotal);
  const globalCompetitionId = process.env.REACT_APP_GLOBAL_LEAGUE_ID;
  const isGlobal = leagueId === globalCompetitionId;

  const { mutate: submitLineup, isLoading: isSubmitLineupLoading } =
    useSubmitLineup();

  const {
    data: eventSubmission,
    isLoading: isFetchLineupsLoading,
    error: fetchSubmissionError,
  } = useFetchUserEventLineup(leagueId, eventId, userId, true);

  const {
    data: eventDetails,
    isLoading: eventLoading,
    error: eventError,
  } = useEventDetails(eventId);

  const fighters =
    eventDetails && eventDetails.Fights?.flatMap((fight) => fight.Fighters);
  const isLineupComplete = lineup.every((fighter) => fighter !== null);
  const hasLotn = lotn !== null;
  const alternatesComplete = alternateSelections.length === 2;
  const alternateLOTNSelected = !!alternateLOTN;

  const handleSubmitButtonClick = () => {
    if (!isLineupComplete) {
      toast.warning("Please select all fighters for the lineup", {
        position: "top-center",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
      return;
    }
    if (!hasLotn) {
      toast.warning("Please select a lock of the night", {
        position: "top-center",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
      return;
    }
    if (!alternatesComplete) {
      toast.warning("Please select two alternate fighters", {
        position: "top-center",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
      return;
    }
    if (!alternateLOTNSelected) {
      toast.warning("Please select an alternate lock of the night", {
        position: "top-center",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
      return;
    }
    handleSubmit(); // Only call handleSubmit if all conditions are met
  };

  const handleSubmit = () => {
    setShowSubmissionModal(true);
  };

  const removeFromLineup = (index) => {
    const newLineup = [...lineup];
    newLineup[index] = null;
    dispatch(setLineup(newLineup));
    updateProjectedTotal(newLineup);
    if (lineup[index] && lineup[index].FighterId === lotn) {
      dispatch(setLotn(null)); // Clear LOTN if removed fighter was the LOTN
    }
    if (lineup[index] && lineup[index].FighterId === alternateLOTN) {
      dispatch(setAlternateLotn(null)); // Clear LOTN if removed fighter was the LOTN
    }
  };

  const addAlternate = (fighter) => {
    // Allow up to two alternates
    if (
      alternateSelections.length < 2 &&
      !alternateSelections.includes(fighter)
    ) {
      dispatch(setAlternateSelections([...alternateSelections, fighter]));
    } else {
      toast.error("You can only select up to two alternates.");
    }
  };

  const removeAlternate = (fighterId) => {
    dispatch(
      setAlternateSelections(
        alternateSelections.filter((f) => f.FighterId !== fighterId)
      )
    );
    if (alternateLOTN && alternateLOTN.FighterId === fighterId) {
      dispatch(setAlternateLotn(null));
    }
  };

  const updateLineupOrder = (newLineup) => {
    dispatch(setLineup(newLineup));
    updateProjectedTotal(newLineup);
  };

  const toggleLotn = (fighterId) => {
    const newLotn = lotn === fighterId ? null : fighterId; // Determine the new LOTN
    dispatch(setLotn(newLotn)); // Update the LOTN state
    setTimeout(() => updateProjectedTotal(lineup, newLotn), 0);
  };

  const toggleAlternateLOTN = (fighter) => {
    if (alternateLOTN && alternateLOTN.FighterId === fighter.FighterId) {
      dispatch(setAlternateLotn(null)); // Deselect if already selected as LOTN
    } else {
      dispatch(setAlternateLotn(fighter)); // Set as LOTN; ensure object structure is correct
    }
  };

  const updateProjectedTotal = (currentLineup, currentLotn = lotn) => {
    const total = currentLineup.reduce((acc, fighter, index) => {
      if (fighter) {
        let score = calculateScoreForSlot(fighter.Moneyline, index);
        // Double the score if the current fighter is the LOTN
        if (fighter.FighterId === currentLotn) {
          score *= 2;
        }
        return acc + score;
      }
      return acc;
    }, 0);
    dispatch(setProjectedTotal(total));
  };

  const handleConfirmSubmit = () => {
    setShowSubmissionModal(false);
    handleSubmitLineup();
  };

  const handleSubmitLineup = () => {
    if (!lotn) {
      alert("Please select a Lock of the Night.");
      return;
    }

    const selections = lineup
      .map(
        (slot, index) =>
          slot && {
            fighter: `${slot.FirstName} ${slot.LastName}`,
            fighterId: slot.FighterId,
            moneyline: slot.Moneyline,
            score: calculateScoreForSlot(slot.Moneyline, index),
            scores: calculateScores(slot.Moneyline, index),
            lotn: slot.FighterId === lotn,
            alternateLotn: slot.FighterId === alternateLOTN,
            position: index + 1,
          }
      )
      .filter(Boolean);

    const alternates = alternateSelections
      .map(
        (slot, index) =>
          slot && {
            fighter: `${slot.FirstName} ${slot.LastName}`,
            fighterId: slot.FighterId,
            moneyline: slot.Moneyline,
            scores: calculateScores(slot.Moneyline, index),
            lotn: slot.FighterId === alternateLOTN,
            alternateLotn: slot.FighterId === alternateLOTN,
          }
      )
      .filter(Boolean);

    submitLineup(
      {
        userId,
        eventId,
        eventName,
        selections,
        leagueId,
        alternates,
        projectedTotal,
      },
      {
        onSuccess: () => {
          // toast.success("Lineup submitted successfully!");
          // navigate("/league/events");
          // clearLineupState();
        },
        onError: (error) =>
          toast.error(`Error submitting lineup: ${error.message}`),
      }
    );
  };

  const handleSubmitGlobalLineup = () => {
    if (!lotn) {
      alert("Please select a Lock of the Night.");
      return;
    }

    const globalLeagueId = globalCompetitionId;

    const selections = lineup
      .map(
        (slot, index) =>
          slot && {
            fighter: `${slot.FirstName} ${slot.LastName}`,
            fighterId: slot.FighterId,
            moneyline: slot.Moneyline,
            score: calculateScoreForSlot(slot.Moneyline, index),
            scores: calculateScores(slot.Moneyline, index),
            lotn: slot.FighterId === lotn,
            alternateLotn: slot.FighterId === alternateLOTN,
            position: index + 1,
          }
      )
      .filter(Boolean);

    const alternates = alternateSelections
      .map(
        (slot, index) =>
          slot && {
            fighter: `${slot.FirstName} ${slot.LastName}`,
            fighterId: slot.FighterId,
            moneyline: slot.Moneyline,
            scores: calculateScores(slot.Moneyline, index),
            lotn: slot.FighterId === alternateLOTN,
            alternateLotn: slot.FighterId === alternateLOTN,
          }
      )
      .filter(Boolean);

    submitLineup(
      {
        userId,
        eventId,
        eventName,
        selections,
        leagueId: globalLeagueId,
        alternates,
        projectedTotal,
      },
      {
        onSuccess: () => {
          // toast.success("Lineup submitted successfully!");
          // navigate("/league/events");
          clearLineupState();
        },
        onError: (error) =>
          toast.error(`Error submitting lineup: ${error.message}`),
      }
    );
  };

  const clearLineupState = () => {
    dispatch(resetLineup());
    dispatch(resetAlternateSelections());
    dispatch(resetLotn());
    dispatch(resetAlternateLotn());
    dispatch(resetProjectedTotal());
  };

  const isFighterOrOpponentInLineup = (fighterIndex) => {
    const opponentIndex =
      fighterIndex % 2 === 0 ? fighterIndex + 1 : fighterIndex - 1;
    return lineup.some(
      (slot) =>
        slot &&
        (slot.FighterId === fighters[fighterIndex].FighterId ||
          slot.FighterId === fighters[opponentIndex].FighterId)
    );
  };

  const isFighterOrOpponentInAlternateLineup = (fighterIndex) => {
    const opponentIndex =
      fighterIndex % 2 === 0 ? fighterIndex + 1 : fighterIndex - 1;
    return alternateSelections.some(
      (slot) =>
        slot &&
        (slot.FighterId === fighters[fighterIndex].FighterId ||
          slot.FighterId === fighters[opponentIndex].FighterId)
    );
  };

  return (
    <LineupContext.Provider
      value={{
        toggleLotn,
        toggleAlternateLOTN,
        updateLineupOrder,
        updateProjectedTotal,
        handleSubmitButtonClick,
        handleSubmit,
        showSubmissionModal,
        setShowSubmissionModal,
        removeFromLineup,
        addAlternate,
        removeAlternate,
        handleConfirmSubmit,
        isSubmitLineupLoading,
        isLineupComplete,
        eventSubmission,
        isFetchLineupsLoading,
        fetchSubmissionError,
        eventDetails,
        eventError,
        eventLoading,
        fighters,
        isFighterOrOpponentInLineup,
        isFighterOrOpponentInAlternateLineup,
        globalCompetitionId,
        isGlobal,
        userId,
        eventId,
        eventName,
        clearLineupState,
        handleSubmitGlobalLineup,
      }}
    >
      {children}
    </LineupContext.Provider>
  );
};
